import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { Suspense, useState } from "react";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { HubMap } from "~/components/layout/HubMap/HubMap";
import { PageContainer } from "~/components/layout/PageContainer";
import { ONBOARDING_STEPS } from "~/components/one-delivery/onboarding";
import { PayPerUsePlanCard } from "~/components/one-delivery/PayPerUsePlanCard";
import type { pricingOneDeliveryProfileSaveMutation } from "./__generated__/pricingOneDeliveryProfileSaveMutation.graphql";

const saveMutation = graphql`
	mutation pricingOneDeliveryProfileSaveMutation($hubId: UUID!) {
		onedeliveryProfileSave(input: { preferredHubId: $hubId }) {
			userId
			baceId
		}
	}
`;

export const Route = createFileRoute("/one-delivery/onboarding/pricing")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async () => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/onboarding/pricing", show: "sign-in" } });
		}
	},
});

/** Figma "OD - Order Step 1 - Pricing" followed by "OD - Order Step 2 - Select Hub". */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const [hubMapOpen, setHubMapOpen] = useState(false);
	const [save, isSaving] = useAsyncMutation<pricingOneDeliveryProfileSaveMutation>(saveMutation);

	const chooseHub = async (hubId: string, close: () => void) => {
		try {
			await save({ variables: { hubId } });
			close();
			await navigate({ to: "/one-delivery/onboarding/summary" });
		} catch (error) {
			toast.error(i18n.OD_ONB_HUB_SAVE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery/onboarding" search={{ step: ONBOARDING_STEPS }} />
			<div className="flex flex-col gap-10 px-6 pt-4 pb-16">
				<div className="flex flex-col gap-4 text-center">
					<h1 className="font-breit text-2xl font-normal text-neutral-950">
						<T>{i18n.OD_ONB_PRICING_TITLE()}</T>
					</h1>
					<p className="text-base text-neutral-500">{i18n.OD_ONB_PRICING_TEXT()}</p>
				</div>
				<div className="flex flex-col items-center gap-8">
					<Tabs value="pay-per-use" onValueChange={() => toast.info(i18n.OD_ONB_SUBSCRIPTION_SOON())}>
						<TabsList className="h-9 rounded-[10px] bg-neutral-100 p-[3px]">
							<TabsTrigger value="pay-per-use" className="px-2 py-1 font-sans text-sm font-medium">
								{i18n.OD_ONB_TAB_PAY_PER_USE()}
							</TabsTrigger>
							<TabsTrigger value="subscription" className="px-2 py-1 font-sans text-sm font-medium">
								{i18n.OD_ONB_TAB_SUBSCRIPTION()}
							</TabsTrigger>
						</TabsList>
					</Tabs>
					<div className="w-full">
						<PayPerUsePlanCard
							action={
								<Button className="h-9 w-full text-sm font-medium" onClick={() => setHubMapOpen(true)}>
									{i18n.OD_ONB_SELECT_PLAN()}
								</Button>
							}
						/>
					</div>
				</div>
			</div>
			{hubMapOpen && (
				<Suspense fallback={null}>
					<HubMap
						open={hubMapOpen}
						onOpenChange={setHubMapOpen}
						filterService="ONEDELIVERY"
						renderAction={({ id }, close) => (
							<Button className="w-full font-breit font-normal" disabled={isSaving} onClick={() => void chooseHub(id, close)}>
								{isSaving && <LoaderCircle className="size-4 animate-spin" />}
								{i18n.CHOOSE_HUB()}
							</Button>
						)}
					/>
				</Suspense>
			)}
		</PageContainer>
	);
}
